/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import type { ItemInfo } from '../hooks/PRIVATE_usePieChartNav';
import { arcTo, closePath, lineTo, moveTo } from '../utils/PRIVATE_visSVGUtils';
import type { PieChartItem, PieSliceInfo } from './PieChart.types';

type Bounds = {
  x: number;
  y: number;
  width: number;
  height: number;
};

const RADIUS_RATIO = 0.45;
const RADIUS_RATIO_WITH_LABELS = 0.38;
const SLICE_GAP_RATIO = 3;
const MIN_ARC_LENGTH = 1.5;
const START_ANGLE = 90;

/**
 * Converts the angle from degrees to radians.
 * @param degree The angle in degrees
 * @returns
 */
export function degreesToRads(degree: number) {
  return degree * (Math.PI / 180);
}

/**
 * Calculates the angle between two vectors.
 * @param vector1X The x component of first vector.
 * @param vector1Y The y component of first vector.
 * @param vector2X The x component of second vector.
 * @param vector2Y The y component of second vector.
 * @returns
 */
export function calculateAngleBetweenTwoVectors(
  vector1X: number,
  vector1Y: number,
  vector2X: number,
  vector2Y: number
) {
  const angle = Math.atan2(vector2Y, vector2X) - Math.atan2(vector1Y, vector1X);
  return angle < 0 ? angle + 2 * Math.PI : angle;
}

/**
 * Get the center coord of the pie chart.
 * @param availSpace The available space.
 * @returns
 */
export function getPieCenter(availSpace: Bounds) {
  return {
    cx: availSpace.x + Math.floor(availSpace.width / 2),
    cy: availSpace.y + Math.floor(availSpace.height / 2)
  };
}

/**
 * Returns the radius of the pie chart.
 * @param availSpace The available space.
 * @param labelPosition The position of labels.
 * @returns
 */
export function getRadius(availSpace: Bounds, isLabelOutside: boolean) {
  const radiusScale = isLabelOutside ? RADIUS_RATIO_WITH_LABELS : RADIUS_RATIO;
  return Math.floor(Math.min(availSpace.width, availSpace.height) * radiusScale);
}

/**
 * Returns the total value of the pie chart.
 * @param items
 * @param totalValue
 */
export function getPieChartInfo(items: PieChartItem[], hiddenIds?: Set<number | string>) {
  return items.reduce(
    ({ totalValue, isLabelOutside }, item) => {
      return {
        totalValue: totalValue + (hiddenIds?.has(item.id) ? 0 : item.value),
        isLabelOutside
      };
    },
    { totalValue: 0, isLabelOutside: false }
  );
}

/**
 * Returns the startAngle and angleOffset for the rendered items.
 * @param items
 * @param totalValue
 */
export function getSliceInfo(items: PieChartItem[], totalValue: number) {
  let anchorOffset = START_ANGLE;
  let percentage;
  const sliceInfo = [];
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    const value = item.value;
    if (value === totalValue) {
      percentage = 100;
    } else if (totalValue === 0) {
      percentage = 0;
    } else {
      // if value is not 100%, cap it at 99.99%
      percentage = Math.min((value / totalValue) * 100, 99.99);
    }
    const arc = percentage * 3.6;
    let angle = anchorOffset - arc;
    if (angle < 0) angle += 360;
    sliceInfo.push({
      index: i,
      startAngle: angle,
      angleExtent: arc,
      ...item
    });

    anchorOffset = angle;
  }
  return sliceInfo;
}

/**
 * Returns the pie slice gaps.
 * @param  pieSliceGap The ratio of maximum supported pie slice gap.
 * @returns
 */
export function getPieSliceGap(pieSliceGap: number) {
  return SLICE_GAP_RATIO * pieSliceGap;
}

/**
 * Returns the array of layouts to render without skipping.
 * @param layouts
 * @param hasLargeItemCount
 * @param radius
 * @returns
 */
export function getRenderedSlices(
  itemInfo: PieSliceInfo[],
  hasLargeItemCount: boolean,
  radius: number
): PieSliceInfo[] {
  if (!hasLargeItemCount) {
    return itemInfo;
  }

  const perimeter = 2 * Math.PI * radius;
  let prevEndCoord = 0;

  return itemInfo.filter(({ startAngle, angleExtent }) => {
    const arcLength = (angleExtent / 360) * perimeter;
    const endCoord = ((angleExtent + startAngle) / 360) * perimeter;
    if (arcLength < MIN_ARC_LENGTH && Math.abs(prevEndCoord - endCoord) < MIN_ARC_LENGTH) {
      return false;
    } else {
      prevEndCoord = endCoord;
      return true;
    }
  });
}

/**
 * Returns the path of the pie slice wedge.
 * @param inset The inset.
 * @param cx The x coordinate of the center of the pie chart.
 * @param cy The y coordinate of the center of the pie chart.
 * @param radiusX The x radius of the slice.
 * @param radiusY The y radius of the slice.
 * @param innerRadius The inner radius.
 * @param angleExtent The angle extent.
 * @param wedgeGap The gap beteen the pie slices.
 * @param startAngle The start angle of the pie slice.
 * @returns
 */
export function makeWedgePath(
  inset: number,
  cx: number,
  cy: number,
  radiusX: number,
  radiusY: number,
  innerRadius: number,
  angleExtent: number,
  wedgeGap: number,
  startAngle: number
) {
  const rx = Math.max(radiusX - inset, 0);
  const ry = Math.max(radiusY - inset, 0);
  const gap = angleExtent == 360 || rx < inset ? 0 : wedgeGap + 2 * inset;

  const ir = innerRadius ? innerRadius + inset : 0;
  // if angle extent is not 360, cap it  at 359.99
  const angleExtentRads = angleExtent == 360 ? degreesToRads(359.99) : degreesToRads(angleExtent);

  const startAngleRads = degreesToRads(startAngle);
  const dataItemGaps = gap / 2;

  const gapAngle = dataItemGaps < rx ? Math.asin(dataItemGaps / rx) : 0;
  const centerLineAngle = -angleExtentRads / 2 - startAngleRads;

  // cap at 5* dataItemsGap. logic from legacy dvt toolkit
  const distanceToStartPointX = Math.min(
    dataItemGaps * 5,
    angleExtentRads > 0 ? Math.abs(dataItemGaps / Math.sin(angleExtentRads / 2)) : 0
  );
  const distanceToStartPointY = rx == 0 ? distanceToStartPointX : (distanceToStartPointX * ry) / rx;

  const startPointX = cx + Math.cos(centerLineAngle) * distanceToStartPointX;
  const startPointY = cy + Math.sin(centerLineAngle) * distanceToStartPointY;

  const arcPointX = cx + Math.cos(-gapAngle - startAngleRads) * rx;
  const arcPointY = cy + Math.sin(-gapAngle - startAngleRads) * ry;

  const arcPoint2X = cx + Math.cos(-startAngleRads - angleExtentRads + gapAngle) * rx;
  const arcPoint2Y = cy + Math.sin(-startAngleRads - angleExtentRads + gapAngle) * ry;

  let outerAngle = calculateAngleBetweenTwoVectors(
    arcPoint2X - cx,
    arcPoint2Y - cy,
    arcPointX - cx,
    arcPointY - cy
  );
  outerAngle = Math.min(outerAngle, angleExtentRads);
  let pathCommands: string;
  if (ir > 0) {
    const innerGapAngle = dataItemGaps < ir ? Math.asin(dataItemGaps / ir) : 0;
    const innerPointX = cx + Math.cos(-innerGapAngle - startAngleRads) * ir;
    const innerPointY = cy + Math.sin(-innerGapAngle - startAngleRads) * ir;

    const innerPoint2X = cx + Math.cos(-startAngleRads - angleExtentRads + innerGapAngle) * ir;
    const innerPoint2Y = cy + Math.sin(-startAngleRads - angleExtentRads + innerGapAngle) * ir;

    let innerAngle = calculateAngleBetweenTwoVectors(
      innerPoint2X - cx,
      innerPoint2Y - cy,
      innerPointX - cx,
      innerPointY - cy
    );
    innerAngle = Math.min(innerAngle, outerAngle, angleExtentRads);

    if (angleExtent == 360) {
      pathCommands = moveTo(arcPoint2X, arcPoint2Y);
      pathCommands += arcTo(rx, ry, angleExtentRads, 1, arcPointX, arcPointY);
      pathCommands += lineTo(arcPoint2X, arcPoint2Y);
      pathCommands += moveTo(innerPointX, innerPointY);
      pathCommands += arcTo(ir, ir, angleExtentRads, 0, innerPoint2X, innerPoint2Y);
    } else {
      pathCommands = moveTo(innerPoint2X, innerPoint2Y);
      pathCommands += lineTo(arcPoint2X, arcPoint2Y);
      pathCommands += arcTo(rx, ry, outerAngle, 1, arcPointX, arcPointY);
      pathCommands += lineTo(innerPointX, innerPointY);
      pathCommands += arcTo(ir, ir, innerAngle, 0, innerPoint2X, innerPoint2Y);
    }
  } else {
    if (angleExtent == 360) {
      pathCommands = moveTo(arcPoint2X, arcPoint2Y);
      pathCommands += arcTo(rx, ry, angleExtentRads, 1, arcPointX, arcPointY);
    } else {
      pathCommands = moveTo(startPointX, startPointY);
      pathCommands += lineTo(arcPoint2X, arcPoint2Y);
      pathCommands += arcTo(rx, ry, outerAngle, 1, arcPointX, arcPointY);
    }
  }

  pathCommands += closePath();
  return pathCommands;
}

/**
 * Returns the pie chart center context.
 * @param cx The x coordinate of the center of the pie chart.
 * @param cy The y coordinate of the center of the pie chart.
 * @param innerRadius The inner radius of the pie chart.
 * @returns
 */
export function getCenterContext(cx: number, cy: number, innerRadius: number) {
  return {
    innerBounds: {
      width: Math.sqrt(2) * innerRadius,
      height: Math.sqrt(2) * innerRadius,
      x: cx - innerRadius / Math.sqrt(2),
      y: cy - innerRadius / Math.sqrt(2)
    },
    outerBounds: {
      width: 2 * innerRadius,
      height: 2 * innerRadius,
      x: cx - innerRadius,
      y: cy - innerRadius
    }
  };
}

/**
 * Returns the position of the datatip for a given slice.
 * @param startAngle The start angle of the pie slice.
 * @param angleExtent The angle extent of the pie slice.
 * @param cx The x coordinate of the center of the pie slice.
 * @param cy The y coordinate of the center of the pie slice.
 * @param innerRadius The inner radius.
 * @param radius The radius of the pie slice.
 * @returns
 */
export function getPieItemCenterCoord(
  startAngle: number,
  angleExtent: number,
  cx: number,
  cy: number,
  innerRadius: number,
  radius: number
) {
  const angle = startAngle + angleExtent / 2;
  const theta = (angle / 180) * Math.PI;
  const r = (innerRadius + radius) / 2;

  return {
    x: cx + r * Math.cos(theta),
    y: cy - r * Math.sin(theta),
    width: 0,
    height: 0
  };
}

/**
 * Returns the element bound for active item.
 * @param renderedSlices The array of info for rendered slices.
 * @param cx The x coordinate of pie chart center.
 * @param cy The y coordinate of pie chart center.
 * @param ir The inner radius of the pie chart.
 * @param radius The radius of the pie chart.
 * @param activeItem The active item.
 * @returns 
 */
export function getElementBounds (
  renderedSlices: PieSliceInfo[],
  cx: number,
  cy: number,
  ir: number,
  radius: number,
  activeItem?: ItemInfo,
) {
  return activeItem
  ? getPieItemCenterCoord(
    renderedSlices[activeItem.itemIndex].startAngle,
    renderedSlices[activeItem.itemIndex].angleExtent,
    cx,
    cy,
    ir,
    radius
  )
  : undefined;
}

/**
 * Returns default border color for datatip.
 * @param colors The color ramp.
 * @param activeItem The active item info.
 * @param activeData The active pie chart data item.
 * @returns 
 */
export function getDefaultBorderColor(
  colors: string[],
  activeItem?: ItemInfo,
  activeData?: PieChartItem,
) {
  return  activeData?.color || activeItem?.itemIndex != undefined
  ? colors[activeItem!.itemIndex]
  : undefined;
}